/**
 * Official mailboxes and communications (E01, ALC-4771): the mailboxes where an authority posts
 * official communications to a company (the DTE of the RFB, the DTE of the CBS, municipal mailboxes),
 * who reads each one, the rule that gives the days to take notice (ciência), and the communications
 * received. Entry is manual; reading the mailbox automatically belongs to E07.
 *
 * A federal mailbox (DTE) belongs to the Taxpayer and is written through its head office; a
 * municipal mailbox belongs to the establishment.
 */
import type { ObligationResponsibleVacancy } from './fiscal-obligations.js';
import type {
  AcknowledgmentKind,
  MailboxDeadlineDayType,
  ObligationScopeType,
  OfficialCommunicationOutcome,
  OfficialCommunicationStatus,
  OfficialMailboxKind,
} from './generated/enums.js';

export const OFFICIAL_MAILBOX_KINDS = ['RFB_DTE', 'CBS_DTE', 'MUNICIPAL'] as const;

/** A DTE belongs to the Taxpayer; a municipal mailbox to the establishment that deals with the city. */
export function mailboxScopeType(kind: OfficialMailboxKind): ObligationScopeType {
  return kind === 'MUNICIPAL' ? 'ENTITY' : 'TAXPAYER';
}

export const MAILBOX_DEADLINE_DAY_TYPES = ['CALENDAR', 'BUSINESS'] as const;
export const OFFICIAL_COMMUNICATION_STATUSES = [
  'RECEIVED',
  'ACKNOWLEDGED',
  'FORWARDED',
  'CLOSED',
] as const;
export const OFFICIAL_COMMUNICATION_OUTCOMES = [
  'NO_ACTION_REQUIRED',
  'RESPONDED',
  'REGULARIZED',
  'CONTESTED',
  'OTHER',
] as const;

/** Days ahead of the deadline a communication without ciência counts as near it. */
export const COMMUNICATION_NEAR_DEADLINE_DAYS = 7;

/** One version of the rule that gives the days to take notice of a communication. */
export interface MailboxRuleItem {
  ruleId: string;
  /** 1, 2, 3...: each new rule of the mailbox is the next version. */
  version: number;
  validFrom: string;
  validTo: string | null;
  deadlineDays: number;
  dayType: MailboxDeadlineDayType;
  /** The legal basis or the instruction the days come from. */
  source: string;
}

export interface MailboxResponsibleState {
  userId: string | null;
  userName: string | null;
  /** Why the person named cannot act, or null when they can (or the vacancy when nobody is named). */
  vacancy: ObligationResponsibleVacancy | null;
}

export interface OfficialMailboxItem {
  mailboxId: string;
  kind: OfficialMailboxKind;
  scopeType: ObligationScopeType;
  /** The Taxpayer or establishment the mailbox belongs to. */
  scopeId: string;
  label: string;
  isActive: boolean;
  responsible: MailboxResponsibleState;
  /** The rule in force today; null when the first rule starts later. */
  currentRule: MailboxRuleItem | null;
  rules: MailboxRuleItem[];
  /** Communications still waiting for ciência. */
  pendingCommunications: number;
  /** The earliest deadline among them. */
  nearestDeadlineOn: string | null;
  /** This establishment's page may change it (a DTE only on the head office). */
  editable: boolean;
  updatedAt: string | null;
}

export type MailboxReadOnlyReason = 'NOT_HEAD_OFFICE' | 'NO_TAXPAYER';

export interface EntityMailboxesResponse {
  entityId: string;
  asOf: string;
  taxpayer: {
    taxpayerId: string;
    name: string;
    headOfficeEntityId: string | null;
    isHeadOffice: boolean;
  } | null;
  /** Why the DTE mailboxes are read only here; null when they can be changed. */
  taxpayerLevelReadOnlyReason: MailboxReadOnlyReason | null;
  mailboxes: OfficialMailboxItem[];
}

/** Where a communication that still has no ciência stands against its deadline. */
export type CommunicationDeadlineState = 'OK' | 'NEAR' | 'TACIT_ELAPSED';

export interface CommunicationActor {
  userId: string | null;
  userName: string | null;
  /** When it was recorded (ISO instant). */
  at: string;
}

export interface OfficialCommunicationItem {
  communicationId: string;
  mailboxId: string;
  subject: string;
  referenceNumber: string | null;
  /** The day the authority made it available. */
  availableOn: string;
  /** The deadline and the rule version it was calculated with, kept as it was then. */
  deadline: {
    deadlineOn: string;
    ruleId: string;
    ruleVersion: number;
    ruleSource: string;
    deadlineDays: number;
    dayType: MailboxDeadlineDayType;
  };
  status: OfficialCommunicationStatus;
  /** Only for a communication still without ciência. */
  deadlineState: CommunicationDeadlineState | null;
  /** Days from today to the deadline; negative once it passed. Only without ciência. */
  daysLeft: number | null;
  received: CommunicationActor;
  acknowledgment: {
    /** The day the person took notice. */
    acknowledgedOn: string;
    /** Express when taken before the deadline ended; tacit when the deadline had already passed. */
    kind: AcknowledgmentKind;
    /** The day ciência has effect: the day it was taken, or the deadline when that came first. */
    effectiveOn: string;
    by: CommunicationActor;
  } | null;
  forwarding: {
    toUserId: string;
    toUserName: string | null;
    note: string | null;
    by: CommunicationActor;
  } | null;
  closing: {
    outcome: OfficialCommunicationOutcome;
    note: string;
    by: CommunicationActor;
  } | null;
}

export interface MailboxCommunicationsResponse {
  mailboxId: string;
  asOf: string;
  communications: OfficialCommunicationItem[];
  /** Communications of the mailbox under the same status filter, across every page. */
  total: number;
  page: number;
  pageSize: number;
}

export interface RegisterMailboxRequest {
  kind: OfficialMailboxKind;
  /** Required for a municipal mailbox (which city); a DTE takes its name from the kind. */
  label?: string;
  responsibleUserId: string;
  rule: MailboxRuleInput;
}

export interface MailboxRuleInput {
  validFrom: string;
  deadlineDays: number;
  dayType: MailboxDeadlineDayType;
  source: string;
}

export interface UpdateMailboxRequest {
  /** A user to read the mailbox, or null to leave it with nobody. */
  responsibleUserId?: string | null;
  isActive?: boolean;
  label?: string;
}

export interface RecordCommunicationRequest {
  subject: string;
  referenceNumber?: string;
  availableOn: string;
}

export interface AcknowledgeCommunicationRequest {
  /** The day the person took notice; defaults to today. */
  acknowledgedOn?: string;
}

export interface ForwardCommunicationRequest {
  toUserId: string;
  note?: string;
}

export interface CloseCommunicationRequest {
  outcome: OfficialCommunicationOutcome;
  note: string;
}

/** A communication without ciência whose deadline is near or already passed. */
export interface NearDeadlineCommunicationItem {
  communicationId: string;
  mailboxId: string;
  mailboxKind: OfficialMailboxKind;
  mailboxLabel: string;
  scopeType: ObligationScopeType;
  taxpayerId: string | null;
  /** The establishment that owns the mailbox, or the head office of the Taxpayer that does. */
  entityId: string;
  subject: string;
  availableOn: string;
  deadlineOn: string;
  daysLeft: number;
  state: Exclude<CommunicationDeadlineState, 'OK'>;
  /** Who reads the mailbox and can act now; null when the mailbox has nobody who can. */
  responsibleUserId: string | null;
  vacancy: ObligationResponsibleVacancy | null;
}

export interface NearDeadlineCommunicationsResponse {
  asOf: string;
  withinDays: number;
  items: NearDeadlineCommunicationItem[];
}
